import React, { useCallback, useEffect, useState } from 'react';
import { useI18n } from '../../context/i18n';
import { playsTranslations } from './playsCopy';
import { fetchPlays, pausePlay, archivePlay, fetchEntitlements } from './playsApi';
import PlaysMeter from './PlaysMeter';
import LaunchPlayButton from './LaunchPlayButton';
import './PlaysDashboard.css';

const fmtMoney = (value) => {
  const n = Number(value || 0);
  if (!Number.isFinite(n)) return '$0';
  if (Math.abs(n) >= 1000) return `$${(n / 1000).toFixed(1)}K`;
  return `$${n.toFixed(0)}`;
};

// Plays dashboard: the billing truth. "What appears active here counts,
// nothing else does" (spec section 6). Lives inside the Opportunities /
// Revenue Finder area per the agreed nav (Revenue Engine + Golden
// Opportunities). Lists every Play with name, objective, type badge
// (One-time / Always-on), status, campaigns run, 30-day correlated revenue
// (labeled correlational, never causal), and pause/archive toggles.
export default function PlaysDashboard({ token, onNavigateToPricing }) {
  const { language } = useI18n();
  const copy = playsTranslations[language]?.dashboard || playsTranslations.en.dashboard;

  const [plays, setPlays] = useState([]);
  const [overCapacity, setOverCapacity] = useState(null);
  const [entitlements, setEntitlements] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyPlayId, setBusyPlayId] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const load = useCallback(async () => {
    if (!token) return;
    setIsLoading(true);
    setError('');
    try {
      const [playsData, entitlementsData] = await Promise.all([
        fetchPlays({ token }),
        fetchEntitlements({ token }),
      ]);
      setPlays(playsData?.plays || []);
      setOverCapacity(playsData?.over_capacity || null);
      setEntitlements(entitlementsData);
    } catch (err) {
      setError(err?.message || 'Could not load your Plays.');
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const handlePause = async (playId) => {
    setBusyPlayId(playId);
    try {
      await pausePlay({ token, playId });
      setRefreshKey((k) => k + 1);
    } catch (err) {
      setError(err?.message || 'Could not pause this Play.');
    } finally {
      setBusyPlayId(null);
    }
  };

  const handleArchive = async (playId) => {
    setBusyPlayId(playId);
    try {
      await archivePlay({ token, playId });
      setRefreshKey((k) => k + 1);
    } catch (err) {
      setError(err?.message || 'Could not archive this Play.');
    } finally {
      setBusyPlayId(null);
    }
  };

  return (
    <div className="plays-dashboard">
      <div className="plays-dashboard-header">
        <div>
          <h2 className="plays-dashboard-title">{copy.title}</h2>
          <p className="plays-dashboard-subtitle">{copy.subtitle}</p>
        </div>
      </div>

      <PlaysMeter token={token} entitlements={entitlements} onNavigateToPricing={onNavigateToPricing} refreshKey={refreshKey} />

      {overCapacity && (
        <div className="plays-dashboard-over-capacity" role="status">
          <strong>{copy.overCapacityTitle}</strong>
          <p>{copy.overCapacityBody(overCapacity.active, overCapacity.allowed)}</p>
        </div>
      )}

      {error && <div className="plays-dashboard-error">{error}</div>}

      {isLoading && plays.length === 0 ? (
        <div className="plays-dashboard-loading">Loading your Plays...</div>
      ) : plays.length === 0 ? (
        <div className="plays-dashboard-empty">{copy.empty}</div>
      ) : (
        <div className="plays-dashboard-list">
          {plays.map((play) => {
            const isBusy = busyPlayId === play.id;
            const isDraft = play.status === 'draft';
            const isActive = play.status === 'active';
            const isPaused = play.status === 'paused';
            const typeLabel = play.type === 'always_on' ? copy.typeAlwaysOn : copy.typeOneTime;
            const statusLabel = copy.status[play.status] || play.status;

            return (
              <div key={play.id} className={`plays-dashboard-card status-${play.status}`}>
                <div className="plays-dashboard-card-top">
                  <div className="plays-dashboard-card-name-row">
                    <span className="plays-dashboard-card-name">{play.name}</span>
                    <span className={`plays-dashboard-type-badge type-${play.type}`}>{typeLabel}</span>
                    <span className={`plays-dashboard-status-badge status-${play.status}`}>{statusLabel}</span>
                  </div>
                  {play.objective && <p className="plays-dashboard-card-objective">{play.objective}</p>}
                </div>

                <div className="plays-dashboard-card-stats">
                  <span>{copy.campaignsRun(play.campaigns_run || 0)}</span>
                  {play.correlated_revenue_30d != null && (
                    <span title={copy.correlatedRevenueNote}>
                      {copy.correlatedRevenue(fmtMoney(play.correlated_revenue_30d))}
                    </span>
                  )}
                </div>

                <div className="plays-dashboard-card-actions">
                  {isDraft && (
                    <LaunchPlayButton
                      token={token}
                      playId={play.id}
                      entitlements={entitlements}
                      canActivate={entitlements?.can_activate}
                      onActivated={() => setRefreshKey((k) => k + 1)}
                      onNavigateToPricing={onNavigateToPricing}
                    />
                  )}
                  {isActive && (
                    <button
                      type="button"
                      className="plays-dashboard-action-btn"
                      disabled={isBusy}
                      onClick={() => handlePause(play.id)}
                    >
                      {copy.pause}
                    </button>
                  )}
                  {(isActive || isPaused) && (
                    <button
                      type="button"
                      className="plays-dashboard-action-btn plays-dashboard-action-btn-danger"
                      disabled={isBusy}
                      onClick={() => handleArchive(play.id)}
                    >
                      {copy.archive}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
